import { apiRequest, ApiError } from "./apiClient"

/**
 * SIA (Supplier Item Authority) data for a supplier+item pairing at a facility:
 * purchasing UOM, case pack, lead time, and — critically for PO creation — the
 * contract `unitPrice` per purchasing unit. This is the authority Eclipse reads
 * from rather than computing price itself (see data-access-rules: price comes
 * from CloudAuth/SIA, never from SageX3 SOAP).
 */
export interface SupplierItem {
  facility?: string
  supplierCode?: string
  itemCode?: string
  itemDescription?: string
  /** Stock units per purchasing case (e.g. 40 = 40 LB per case). */
  casePackSize?: number
  /** Stock unit of measure (what's inside the case: LB, EA, OZ). */
  stockUom?: string
  /** Purchasing unit of measure (how you order: CS, EA, LB). */
  purchaseUom?: string
  /** Price per purchasing unit (per case) — used to auto-populate a PO line. */
  unitPrice?: number
  supplierSku?: string
  leadTimeDays?: number
  minOrderQty?: number
  primarySupplier?: boolean
}

/** A supplier that carries an item (SIA relationship), with pack/UOM/price. */
export interface ItemSupplierRelationship {
  supplierCode?: string
  supplierSku?: string
  casePackSize?: number
  stockUom?: string
  purchaseUom?: string
  unitPrice?: number
  leadTimeDays?: number
  minOrderQty?: number
  primarySupplier?: boolean
  lastPriceDate?: string
}

export interface ListItemSuppliersResponse {
  itemCode?: string
  itemDescription?: string
  suppliers?: ItemSupplierRelationship[]
}

/**
 * List the SIA supplier-item relationships for an item at a facility — the
 * suppliers that carry it (real primary supplier + pack/UOM/price). Returns an
 * empty `suppliers` list for items with no managed supplier (manufactured goods,
 * or items not yet in SIA), so the caller renders an honest empty state.
 */
export async function listItemSuppliers(
  facility: string,
  item: string,
): Promise<ListItemSuppliersResponse> {
  return apiRequest<ListItemSuppliersResponse>(
    `/item-suppliers?facility=${encodeURIComponent(facility)}&item=${encodeURIComponent(item)}`,
  )
}

/**
 * Look up the SIA supplier-item pairing for PO line auto-population.
 *
 * Returns null when the supplier doesn't carry the item (404), so the caller
 * can fall back to manual price entry rather than treating an unlinked pairing
 * as a hard error. Other failures propagate.
 */
export async function getSupplierItem(
  facility: string,
  supplier: string,
  item: string,
): Promise<SupplierItem | null> {
  try {
    return await apiRequest<SupplierItem>(
      `/supplier-item?facility=${encodeURIComponent(facility)}` +
        `&supplier=${encodeURIComponent(supplier)}&item=${encodeURIComponent(item)}`,
    )
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return null
    }
    throw err
  }
}
